import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { orpc } from "~/lib/orpc-client";

export const Route = createFileRoute("/university/dashboard")({
  beforeLoad: async () => { await requireAuth(); },
  component: PartnershipDashboard,
});

const ACTION_LABELS: Record<string, string> = {
  "resume_tailor": "Resume Tailoring",
  "cover_letter": "Cover Letter Generation",
  "interview-prep": "Interview Preparation",
  "ats-score": "ATS Scoring",
  "salary-benchmark": "Salary Benchmarking",
  "market-intel": "Market Intelligence",
  "resume-bullets": "Resume Bullets",
  "resume-summary": "Resume Summary",
};

function PartnershipDashboard() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery(
    orpc.university.dashboardStats.queryOptions(),
  );
  const [emailsText, setEmailsText] = useState("");
  const [bulkResult, setBulkResult] = useState<{
    created: number;
    skipped: number;
    rejected: number;
    rejectedEmails: string[];
  } | null>(null);

  const bulkMutation = useMutation(
    orpc.university.bulkVerify.mutationOptions(),
  );

  function handleBulkUpload() {
    if (!data?.partnership.id || !emailsText.trim()) return;
    const emails = emailsText
      .split(/[\n,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);

    bulkMutation.mutate(
      { partnershipId: data.partnership.id, emails },
      {
        onSuccess: (result) => {
          setBulkResult(result);
          setEmailsText("");
          queryClient.invalidateQueries({ queryKey: ["university"] });
        },
      },
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: "var(--bg)" }}>
        <p style={{ color: "var(--text-muted)" }}>Loading dashboard...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: "var(--bg)" }}>
        <div className="text-center">
          <h1 className="text-xl font-bold" style={{ color: "var(--text)" }}>Access Denied</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
            No partnership found for your account. <Link to="/university" className="underline">Learn about partnerships</Link>
          </p>
        </div>
      </div>
    );
  }

  const { partnership, metrics, bulkVerification } = data;
  const maxWeekly = Math.max(...metrics.weeklySignups.map((w) => w.count), 1);

  return (
    <div style={{ backgroundColor: "var(--bg)" }}>
      <section className="mx-auto max-w-5xl px-6 pb-24 pt-12">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>
              Partnership Dashboard — {partnership.name}
            </h1>
            <div className="mt-1 flex items-center gap-3">
              <span
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                style={{ backgroundColor: "var(--accent-subtle)", color: "var(--accent)" }}
              >
                {partnership.tier.replace("_", " ")}
              </span>
            </div>
          </div>
          <button
            onClick={() => navigator.clipboard.writeText(`https://alchema.ai/uni/${partnership.slug}`)}
            className="rounded-[10px] border border-[var(--border)] px-4 py-2 text-sm font-medium hover:bg-[var(--surface-raised)]"
            style={{ color: "var(--text)" }}
          >
            Copy Signup Link
          </button>
        </div>

        {/* Metric Cards */}
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard label="Total Signups" value={metrics.totalSignups} />
          <MetricCard label="Activated Students" value={metrics.activatedUsers} subtitle={`of ${metrics.totalSignups} signups`} />
          <MetricCard
            label="Activation Rate"
            value={`${metrics.activationRate}%`}
            color={metrics.activationRate > 50 ? "green" : metrics.activationRate > 30 ? "yellow" : "red"}
          />
          <MetricCard
            label="Bulk Pre-Verified"
            value={bulkVerification.totalUploaded}
            subtitle={`${bulkVerification.claimed} claimed`}
          />
        </div>

        {/* Weekly Signup Trend */}
        {metrics.weeklySignups.length > 0 && (
          <div
            className="mt-8 rounded-[13px] border border-[var(--border)] p-6"
            style={{ backgroundColor: "var(--surface)", boxShadow: "var(--shadow-sm)" }}
          >
            <h3 className="text-sm font-semibold" style={{ color: "var(--text)" }}>Weekly Signups (Last 8 Weeks)</h3>
            <div className="mt-4 flex items-end gap-2" style={{ height: "120px" }}>
              {metrics.weeklySignups.map((w) => (
                <div key={w.week} className="flex flex-1 flex-col items-center gap-1">
                  <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>
                    {w.count}
                  </span>
                  <div
                    className="w-full rounded-t"
                    style={{
                      backgroundColor: "var(--accent)",
                      height: `${Math.max((w.count / maxWeekly) * 100, 4)}%`,
                      minHeight: "4px",
                    }}
                  />
                  <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                    {w.week.slice(5)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Features */}
        {metrics.topFeatures.length > 0 && (
          <div
            className="mt-6 rounded-[13px] border border-[var(--border)] p-6"
            style={{ backgroundColor: "var(--surface)", boxShadow: "var(--shadow-sm)" }}
          >
            <h3 className="text-sm font-semibold" style={{ color: "var(--text)" }}>Top Features Used</h3>
            <div className="mt-4 space-y-3">
              {metrics.topFeatures.map((f) => {
                const maxUsage = Math.max(...metrics.topFeatures.map((x) => x.usageCount), 1);
                return (
                  <div key={f.feature} className="flex items-center gap-3">
                    <span className="w-40 text-sm" style={{ color: "var(--text-secondary)" }}>
                      {ACTION_LABELS[f.feature] || f.feature}
                    </span>
                    <div className="flex-1">
                      <div
                        className="h-2 rounded-full"
                        style={{
                          backgroundColor: "var(--accent)",
                          width: `${(f.usageCount / maxUsage) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="w-12 text-right text-xs font-medium" style={{ color: "var(--text-muted)" }}>
                      {f.usageCount}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bulk Verification */}
        <div
          className="mt-6 rounded-[13px] border border-[var(--border)] p-6"
          style={{ backgroundColor: "var(--surface)", boxShadow: "var(--shadow-sm)" }}
        >
          <h3 className="text-sm font-semibold" style={{ color: "var(--text)" }}>Bulk Student Verification</h3>
          <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
            {bulkVerification.totalUploaded} emails uploaded, {bulkVerification.claimed} claimed by students
          </p>

          <div className="mt-4">
            <textarea
              value={emailsText}
              onChange={(e) => setEmailsText(e.target.value)}
              rows={5}
              placeholder="Paste student emails (one per line, comma-separated, or semicolon-separated)..."
              className="w-full rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm outline-none focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)]"
            />
            {emailsText.trim() && (
              <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
                {emailsText.split(/[\n,;]+/).filter((e) => e.trim()).length} emails detected
              </p>
            )}
            <button
              onClick={handleBulkUpload}
              disabled={bulkMutation.isPending || !emailsText.trim()}
              className="mt-3 rounded-[10px] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: "var(--text)" }}
            >
              {bulkMutation.isPending ? "Uploading..." : "Upload & Verify"}
            </button>
          </div>

          {bulkResult && (
            <div className="mt-4 rounded-lg border border-[var(--border)] bg-[var(--bg)] p-4 text-sm">
              <p style={{ color: "var(--text)" }}>
                <strong>{bulkResult.created}</strong> created,{" "}
                <strong>{bulkResult.skipped}</strong> skipped,{" "}
                <strong>{bulkResult.rejected}</strong> rejected
              </p>
              {bulkResult.rejectedEmails.length > 0 && (
                <p className="mt-1 text-xs" style={{ color: "var(--text-muted)" }}>
                  Rejected (wrong domain): {bulkResult.rejectedEmails.join(", ")}
                </p>
              )}
            </div>
          )}

          {bulkMutation.isError && (
            <p className="mt-2 text-sm" style={{ color: "var(--error)" }}>
              {(bulkMutation.error as Error)?.message || "Upload failed"}
            </p>
          )}
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs" style={{ color: "var(--text-muted)" }}>
          All data is anonymized. Individual student information is never visible.{" "}
          <Link to="/privacy" className="underline">GDPR Compliance</Link>
        </p>
      </section>
    </div>
  );
}

function MetricCard({
  label,
  value,
  subtitle,
  color,
}: {
  label: string;
  value: string | number;
  subtitle?: string;
  color?: "green" | "yellow" | "red";
}) {
  const colorVar = color === "green"
    ? "var(--success)"
    : color === "yellow"
      ? "var(--warning)"
      : color === "red"
        ? "var(--error)"
        : undefined;

  return (
    <div
      className="rounded-[13px] border border-[var(--border)] p-5"
      style={{ backgroundColor: "var(--surface)", boxShadow: "var(--shadow-sm)" }}
    >
      <p className="text-xs font-medium uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
        {label}
      </p>
      <p
        className="mt-1 text-2xl font-bold"
        style={{ color: colorVar || "var(--text)" }}
      >
        {value}
      </p>
      {subtitle && (
        <p className="mt-0.5 text-xs" style={{ color: "var(--text-muted)" }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}
